import {
  doc,
  setDoc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  Unsubscribe,
} from 'firebase/firestore'
import { db } from '../firebase'

/**
 * Teacher Favorites Service
 *
 * Data model:
 *   studentFavorites/{studentId} {
 *     teacherIds: string[],   // teacher uids the student has favorited
 *     updatedAt: Timestamp,
 *   }
 *
 * The document id is the student's Firebase Auth uid. The Firestore
 * rule on this collection allows read/write only when the caller's uid
 * matches the document id, so each student can only access their own
 * favorites.
 */

const COLLECTION = 'studentFavorites'

export interface StudentFavorites {
  teacherIds: string[]
  updatedAt?: any
}

/**
 * Fetch the current favorites doc for a student. Returns an empty list
 * if the doc does not exist yet (first-time user).
 */
export const getFavorites = async (
  studentId: string,
): Promise<StudentFavorites> => {
  if (!studentId) return { teacherIds: [] }
  const ref = doc(db, COLLECTION, studentId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return { teacherIds: [] }
  const data = snap.data() as StudentFavorites
  return {
    teacherIds: Array.isArray(data.teacherIds) ? data.teacherIds : [],
    updatedAt: data.updatedAt,
  }
}

/**
 * Add a teacher to a student's favorites. Idempotent — Firestore
 * arrayUnion will not create duplicates. Creates the doc on first use
 * via setDoc with merge:true.
 */
export const addFavorite = async (
  studentId: string,
  teacherId: string,
): Promise<void> => {
  if (!studentId || !teacherId) {
    throw new Error('addFavorite requires studentId and teacherId')
  }
  const ref = doc(db, COLLECTION, studentId)
  await setDoc(
    ref,
    {
      teacherIds: arrayUnion(teacherId),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}

/**
 * Remove a teacher from a student's favorites. Idempotent — removing a
 * teacher that isn't present is a no-op.
 */
export const removeFavorite = async (
  studentId: string,
  teacherId: string,
): Promise<void> => {
  if (!studentId || !teacherId) {
    throw new Error('removeFavorite requires studentId and teacherId')
  }
  const ref = doc(db, COLLECTION, studentId)
  await setDoc(
    ref,
    {
      teacherIds: arrayRemove(teacherId),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}

/**
 * Subscribe to real-time updates for a student's favorites doc.
 * Returns the unsubscribe function. Emits an empty list if the doc
 * does not exist yet.
 */
export const subscribeToFavorites = (
  studentId: string,
  onChange: (favorites: StudentFavorites) => void,
  onError?: (err: Error) => void,
): Unsubscribe => {
  if (!studentId) {
    onChange({ teacherIds: [] })
    return () => {}
  }
  const ref = doc(db, COLLECTION, studentId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onChange({ teacherIds: [] })
        return
      }
      const data = snap.data() as StudentFavorites
      onChange({
        teacherIds: Array.isArray(data.teacherIds) ? data.teacherIds : [],
        updatedAt: data.updatedAt,
      })
    },
    (err) => {
      console.error('[favoritesService] subscribeToFavorites error:', err)
      if (onError) onError(err as Error)
    },
  )
}
